import { FunctionComponent } from "react";

import bienveillanceIcon from "../../assets/values/bienveillance.svg";
import engagementIcon from "../../assets/values/engagement.svg";
import faireEquipeIcon from "../../assets/values/faire-equipe.svg";
import respectIcon from "../../assets/values/respect.svg";
import { VALUE_LABELS, ValueCode } from "../../models/OverviewDashboard";
import styles from "./CompactValueStats.module.css";

export type CompactValue = {
    score?: number;
    percentage?: number;
};

export type CompactValueSet = Record<ValueCode, CompactValue>;

interface CompactValueStatsProps {
    title: string;
    values: CompactValueSet;
}

const VALUE_ORDER: ValueCode[] = [
    "bienveillance",
    "engagement",
    "faire-equipe",
    "respect"
];

const VALUE_ICONS: Record<ValueCode, string> = {
    bienveillance: bienveillanceIcon,
    engagement: engagementIcon,
    "faire-equipe": faireEquipeIcon,
    respect: respectIcon
};

const VALUE_CLASSES: Record<ValueCode, string> = {
    bienveillance: styles.bienveillance,
    engagement: styles.engagement,
    "faire-equipe": styles.faireEquipe,
    respect: styles.respect
};

const CompactValueStats: FunctionComponent<CompactValueStatsProps> = ({ title, values }) => (
    <div className={styles.card} aria-label={`Statistiques résumées ${title}`}>
        <strong className={styles.title}>{title}</strong>
        <div className={styles.grid}>
            {VALUE_ORDER.map((code) => {
                const value = values[code];
                const percentage = getPercentage(value);
                const hasData = percentage !== undefined;

                return (
                    <div
                        key={code}
                        className={`${styles.metric} ${VALUE_CLASSES[code]}`}
                        title={`${VALUE_LABELS[code]} : ${hasData ? `${percentage}%` : "N/A"}`}
                    >
                        <div className={styles.iconBox}>
                            <img src={VALUE_ICONS[code]} alt="" />
                        </div>
                        <div className={styles.progressTrack} aria-hidden="true">
                            {hasData && <span style={{ width: `${percentage}%` }} />}
                        </div>
                        <span className={styles.percentage}>
                            {hasData ? `${percentage}%` : "N/A"}
                        </span>
                    </div>
                );
            })}
        </div>
    </div>
);

function getPercentage(value: CompactValue): number | undefined {
    if (value.percentage !== undefined && Number.isFinite(value.percentage)) {
        return Math.round(Math.min(Math.max(value.percentage, 0), 100));
    }

    if (value.score !== undefined && Number.isFinite(value.score)) {
        return Math.round(Math.min(Math.max(value.score * 10, 0), 100));
    }

    return undefined;
}

export default CompactValueStats;
